import Core from './core'
import Control from './control'
import Player from './player'
import Terrain from './terrain'
import UI from './ui'
import Audio from './audio'
import BlockDropManager from './drops'
import MobManager from './mobs'
import PlayerAvatar from './player/avatar'
import ExplosionManager from './explosions'

import './style.css'

const core = new Core()
const camera = core.camera
const scene = core.scene
const renderer = core.renderer

const player = new Player()
const audio = new Audio(camera)

const terrain = new Terrain(scene, camera)
const dropManager = new BlockDropManager(scene, player.inventory, terrain)
const mobManager = new MobManager(scene, terrain)
mobManager.spawnDefaultAnimals()

const control = new Control(scene, camera, player, terrain, audio, core)
control.setDropManager(dropManager)
control.setMobManager(mobManager)
mobManager.setDropManager(dropManager)

const explosions = new ExplosionManager(
  scene,
  terrain,
  dropManager,
  mobManager,
  audio,
  {
    getPlayerPosition: () => camera.position,
    damagePlayer: (amount, impulse) => {
      if (player.takeDamage(amount)) {
        control.onDamage?.()
      }
      control.applyWorldImpulse(impulse)
    }
  }
)
control.setExplosionManager(explosions)
mobManager.setCreeperExplosionHandler(position => {
  explosions.explode(
    Math.round(position.x),
    Math.round(position.y),
    Math.round(position.z)
  )
})

const ui = new UI(terrain, control, dropManager)
const avatar = new PlayerAvatar(scene, camera)
avatar.setTerrain(terrain)

if (
  import.meta.env.DEV ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === 'localhost'
) {
  ;(window as Window & {
    __omori2Debug?: {
      terrain: Terrain
      control: Control
      mobManager: MobManager
      player: Player
      explosions: ExplosionManager
      core: Core
    }
  }).__omori2Debug = {
    terrain,
    control,
    mobManager,
    player,
    explosions,
    core
  }
}

document.addEventListener('keydown', (event: KeyboardEvent) => {
  if (!control.active || ui.inventoryUI.isOpen) return

  if (event.key === 'v' || event.key === 'V') {
    avatar.toggleView()
  }
  if (event.key === 't' || event.key === 'T') {
    avatar.nextSkin()
  }
})

// one simulation step. Clamping delta protects physics from the giant frame
// that follows a backgrounded tab — without it, mobs and drops integrate one
// multi-second gravity step and fall through the world.
const MAX_DELTA = 0.1
const onHostileDamage = (amount: number) => {
  if (player.takeDamage(amount)) {
    control.onDamage?.()
  }
}

function tick(delta: number) {
  control.update(delta)
  core.updateDayNight(delta, control.active)
  terrain.update()
  if (control.active) {
    explosions.update(delta)
    dropManager.update(delta, camera.position)
    mobManager.update(
      delta,
      camera,
      onHostileDamage,
      core.isNight,
      () => control.getHoldingItemType()
    )
  }
  ui.update()
  avatar.setHeldItem(control.active ? control.getHoldingItemType() : null)
  avatar.update(control.active, delta, {
    isMoving: control.isMoving(),
    isSprinting: control.isSprinting(),
    isFalling: player.isFalling,
    isMining: control.isMining()
  })
}

// deterministic stepper for the debug console: run N frames of fixed delta
// synchronously (rendering once at the end via the normal loop)
const debugHandle = (window as Window & { __omori2Debug?: object }).__omori2Debug as
  | Record<string, unknown>
  | undefined
if (debugHandle) {
  debugHandle.step = (seconds = 1, frameDelta = 0.05) => {
    const frames = Math.ceil(seconds / frameDelta)
    for (let i = 0; i < frames; i++) {
      tick(frameDelta)
    }
  }
}

// track time for delta
let lastTime = performance.now()

// animation
;(function animate() {
  const now = performance.now()
  const delta = Math.min(MAX_DELTA, (now - lastTime) / 1000)
  lastTime = now

  requestAnimationFrame(animate)

  tick(delta)

  avatar.beforeRender()
  renderer.render(scene, camera)
  avatar.afterRender()
})()
